/**
 * Modal form for creating or editing a single LlmConfig. Two top-level
 * modes (API call / Agent); the form below adapts to the chosen mode and
 * the chosen provider/agent (different models, different params, different
 * auth options).
 */
import { useState, useEffect } from 'react'
import Modal from '@/components/common/Modal'
import Button from '@/components/common/Button'
import {
  PROVIDERS,
  AGENTS,
  TASK_TYPES,
  TASK_LABELS,
  getProvider,
  getModel,
  getAgent,
  type ProviderId,
  type AgentId,
  type ThinkingLevel,
  type TaskType,
} from '@/data/llmCatalog'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type {
  LlmConfig,
  LlmApiConfig,
  LlmAgentConfig,
  TaskModelOverride,
} from '@/types'
import { getLlmRecommendations } from '@/api/settings'
import { useAgentModels } from '@/hooks/useAgentModels'
import OpencodeModelPicker from './OpencodeModelPicker'

interface Props {
  open: boolean
  /** Settings scope a bring-your-own key (OpenCode) is stored on. */
  scope?: 'tenant' | 'user'
  initial: LlmConfig | null
  existingNames: string[]
  onClose: () => void
  onSave: (config: LlmConfig) => void
}

const THINKING_LEVELS: ThinkingLevel[] = ['off', 'low', 'medium', 'high']

export default function LlmConfigEditor({
  open,
  scope = 'tenant',
  initial,
  existingNames,
  onClose,
  onSave,
}: Props) {
  const [mode, setMode] = useState<'api' | 'agent'>('api')
  const [name, setName] = useState('')
  // API mode state
  const [provider, setProvider] = useState<ProviderId>('openai')
  const [model, setModel] = useState('')
  const [temperature, setTemperature] = useState<string>('')
  const [maxTokens, setMaxTokens] = useState<string>('')
  const [thinking, setThinking] = useState<ThinkingLevel>('off')
  // Agent mode state
  const [agent, setAgent] = useState<AgentId>('claude_code')
  const [auth, setAuth] = useState<'api_key' | 'subscription' | 'configured'>('subscription')
  const [agentModel, setAgentModel] = useState('')
  // Agent model catalog — static for Claude/Codex, LIVE from the server's CLI
  // for OpenCode (the providers configured in OpenCode decide it).
  const {
    models: agentModels,
    loading: agentModelsLoading,
    error: agentModelsError,
    refresh: refreshAgentModels,
  } = useAgentModels(mode === 'agent' ? agent : null)

  // Per-task model/thinking overrides carried inside this one config, so a
  // single configuration can route different tasks to different models.
  const [taskModels, setTaskModels] = useState<
    Partial<Record<TaskType, TaskModelOverride>>
  >({})
  const [taskModelsOpen, setTaskModelsOpen] = useState(false)
  // OpenCode's searchable picker: which field it is choosing for — the base
  // model ('base') or one task row — or null when closed.
  const [pickerFor, setPickerFor] = useState<'base' | TaskType | null>(null)
  const [recommending, setRecommending] = useState(false)

  const [error, setError] = useState<string>('')

  // Populate state from `initial` whenever the modal opens.
  useEffect(() => {
    if (!open) return
    if (initial) {
      setMode(initial.mode)
      setName(initial.name)
      setThinking(initial.thinking || 'off')
      setTemperature(
        initial.temperature !== undefined && initial.temperature !== null
          ? String(initial.temperature)
          : '',
      )
      setMaxTokens(
        initial.max_tokens !== undefined && initial.max_tokens !== null
          ? String(initial.max_tokens)
          : '',
      )
      if (initial.mode === 'api') {
        setProvider(initial.provider)
        setModel(initial.model)
      } else {
        setAgent(initial.agent)
        setAuth(initial.auth)
        setAgentModel(initial.model || '')
      }
      const tm = initial.task_models || {}
      setTaskModels(tm)
      setTaskModelsOpen(Object.keys(tm).length > 0)
    } else {
      // Defaults for a fresh config
      setMode('api')
      setName('')
      setProvider('openai')
      setModel(PROVIDERS[0].models[0]?.id || '')
      setTemperature('')
      setMaxTokens('')
      setThinking('off')
      setAgent('claude_code')
      setAuth('subscription')
      setAgentModel('')
      setTaskModels({})
      setTaskModelsOpen(false)
    }
    setError('')
  }, [open, initial])

  // The model catalog applicable to the current mode/provider/agent. Both the
  // base model dropdown and the per-task overrides draw from this list.
  const taskModelOptions =
    mode === 'api' ? getProvider(provider)?.models || [] : agentModels

  // When provider changes, ensure the model selection is valid.
  useEffect(() => {
    if (mode !== 'api') return
    const p = getProvider(provider)
    if (!p) return
    if (!p.models.find((m) => m.id === model)) {
      setModel(p.models[0]?.id || '')
    }
    pruneTaskModels(p.models.map((m) => m.id))
  }, [provider, mode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Same idea for agent mode: switching agent invalidates a model id from a
  // different provider's catalog. Reset to the default-pick sentinel ('').
  useEffect(() => {
    if (mode !== 'agent') return
    // A live catalog (OpenCode) that has not loaded yet — or failed — must not
    // wipe a saved selection: an empty list proves nothing about the id. And
    // OpenCode picks from a searchable catalog (any provider in API-key mode),
    // so the server list is never grounds to drop an OpenCode model.
    if (agentModels.length === 0 || agent === 'opencode') return
    if (agentModel) {
      const valid = agentModels.some((m) => m.id === agentModel)
      if (!valid) setAgentModel('')
    }
    pruneTaskModels(agentModels.map((m) => m.id))
  }, [agent, mode, agentModels]) // eslint-disable-line react-hooks/exhaustive-deps

  // Drop per-task model ids that no longer exist in the new catalog so a
  // stale id from a different provider doesn't silently persist.
  function pruneTaskModels(validIds: string[]) {
    setTaskModels((prev) => {
      let changed = false
      const next: Partial<Record<TaskType, TaskModelOverride>> = {}
      for (const [task, ov] of Object.entries(prev) as [TaskType, TaskModelOverride][]) {
        if (ov?.model && !validIds.includes(ov.model)) {
          const stripped = { ...ov, model: undefined }
          changed = true
          if (stripped.thinking) next[task] = stripped
        } else {
          next[task] = ov
        }
      }
      return changed ? next : prev
    })
  }

  function setTaskOverride(task: TaskType, patch: Partial<TaskModelOverride>) {
    setTaskModels((prev) => {
      const merged: TaskModelOverride = { ...(prev[task] || {}), ...patch }
      const next = { ...prev }
      // Drop the entry entirely once it carries no model and no thinking, so
      // an all-default row isn't persisted as noise.
      if (!merged.model && !merged.thinking) {
        delete next[task]
      } else {
        next[task] = merged
      }
      return next
    })
  }

  // Pre-fill every per-task row with the analysis-backed recommended model +
  // thinking level for the CURRENT provider/agent. Fills the dropdowns only —
  // the user can still tweak any row and must Save for it to take effect.
  async function applyRecommended() {
    setRecommending(true)
    setError('')
    try {
      const recs = await getLlmRecommendations(
        mode === 'api' ? { provider } : { agent },
      )
      const validIds = new Set(taskModelOptions.map((m) => m.id))
      setTaskModels((prev) => {
        const next = { ...prev }
        for (const task of TASK_TYPES) {
          const r = recs[task]
          if (!r || !validIds.has(r.model)) continue
          const patch: TaskModelOverride = { model: r.model }
          if (supportsThinkingColumn && r.thinking && r.thinking !== 'off') {
            patch.thinking = r.thinking as ThinkingLevel
          }
          next[task] = { ...(prev[task] || {}), ...patch }
        }
        return next
      })
      setTaskModelsOpen(true)
    } catch {
      setError('Could not load recommended models for this provider.')
    } finally {
      setRecommending(false)
    }
  }

  function handleSave() {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Name is required.')
      return
    }
    // Name uniqueness — case-insensitive so "GPT 5" and "gpt 5" don't collide.
    const lower = trimmed.toLowerCase()
    const clashesWithOther = existingNames.some(
      (n) => n.toLowerCase() === lower && n !== initial?.name,
    )
    if (clashesWithOther) {
      setError('A configuration with that name already exists.')
      return
    }

    const params: {
      temperature?: number
      max_tokens?: number
      thinking?: ThinkingLevel
      task_models?: Partial<Record<TaskType, TaskModelOverride>>
    } = {}
    if (temperature !== '') {
      const t = parseFloat(temperature)
      if (!Number.isNaN(t)) params.temperature = t
    }
    if (maxTokens !== '') {
      const m = parseInt(maxTokens, 10)
      if (!Number.isNaN(m)) params.max_tokens = m
    }
    if (thinking !== 'off') params.thinking = thinking
    // Only persist non-empty per-task overrides (setTaskOverride already
    // prunes all-default rows), and only against the current catalog.
    const cleanTaskModels: Partial<Record<TaskType, TaskModelOverride>> = {}
    for (const [task, ov] of Object.entries(taskModels) as [TaskType, TaskModelOverride][]) {
      const entry: TaskModelOverride = {}
      if (ov?.model) entry.model = ov.model
      if (ov?.thinking && ov.thinking !== 'off') entry.thinking = ov.thinking
      if (entry.model || entry.thinking) cleanTaskModels[task] = entry
    }
    if (Object.keys(cleanTaskModels).length > 0) params.task_models = cleanTaskModels

    if (mode === 'api') {
      if (!model) {
        setError('Model is required.')
        return
      }
      const cfg: LlmApiConfig = {
        name: trimmed,
        mode: 'api',
        provider,
        model,
        ...params,
      }
      onSave(cfg)
      return
    }

    if (agent === 'opencode' && auth === 'api_key' && !agentModel) {
      setError('OpenCode in API-key mode needs a model — its provider decides which key is used.')
      return
    }
    const cfg: LlmAgentConfig = {
      name: trimmed,
      mode: 'agent',
      agent,
      auth,
      ...(agentModel ? { model: agentModel } : {}),
      ...params,
    }
    onSave(cfg)
  }

  const providerSpec = mode === 'api' ? getProvider(provider) : undefined
  const modelSpec = mode === 'api' ? getModel(provider, model) : undefined
  const agentSpec = mode === 'agent' ? getAgent(agent) : undefined

  // Label for the "inherit" option in the per-task matrix — the config's
  // own base model (or the agent's built-in default when left blank).
  const baseModelLabel =
    mode === 'api'
      ? getModel(provider, model)?.label || model || 'default model'
      : agentModel
        ? agentModels.find((m) => m.id === agentModel)?.label || agentModel
        : 'agent default'
  const taskOverrideCount = Object.values(taskModels).filter(
    (o) => o && (o.model || o.thinking),
  ).length
  const supportsThinkingColumn =
    mode === 'agent'
      ? !!agentSpec?.supports_thinking
      : taskModelOptions.some((m) => m.supports_thinking)

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={initial ? `Edit "${initial.name}"` : 'Add LLM configuration'}
      size="wide"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            {initial ? 'Save changes' : 'Add configuration'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Name */}
        <Field label="Name" hint="A short identifier (e.g. “Claude agent”, “GPT-5 cheap”).">
          <input
            type="text"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="My configuration"
            autoFocus
          />
        </Field>

        {/* Mode */}
        <Field label="Mode" hint="API call hits the provider directly; Agent runs a coding CLI on the server.">
          <SegmentedControl
            value={mode}
            onChange={(v) => setMode(v as 'api' | 'agent')}
            options={[
              { id: 'api', label: 'LLM API call' },
              { id: 'agent', label: 'Agent' },
            ]}
          />
        </Field>

        {/* API-mode fields */}
        {mode === 'api' && (
          <>
            <Field label="Provider">
              <select
                className="input"
                value={provider}
                onChange={(e) => setProvider(e.target.value as ProviderId)}
              >
                {PROVIDERS.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Model">
              <select
                className="input"
                value={model}
                onChange={(e) => setModel(e.target.value)}
              >
                {providerSpec?.models.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </select>
            </Field>
          </>
        )}

        {/* Agent-mode fields */}
        {mode === 'agent' && (
          <>
            <Field label="Agent">
              <select
                className="input"
                value={agent}
                onChange={(e) => {
                  const next = e.target.value as AgentId
                  setAgent(next)
                  // Reset auth default per agent: Claude defaults to
                  // subscription (it's the headline use case);
                  // Codex/Gemini default to api_key since their OAuth path
                  // requires pasting a JSON blob and most users start with a key.
                  // OpenCode defaults to the providers configured in OpenCode.
                  setAuth(
                    next === 'claude_code' ? 'subscription'
                      : next === 'opencode' ? 'configured'
                        : 'api_key',
                  )
                }}
              >
                {AGENTS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              label="Authentication"
              hint={
                agent === 'claude_code'
                  ? 'Subscription uses an OAuth token from `claude setup-token` (your plan is billed). API key uses an Anthropic API key (your Anthropic account is billed).'
                  : agent === 'opencode'
                    ? 'OpenCode providers runs on the providers configured in OpenCode on the server (`opencode auth login`), including the free OpenCode Zen models — no key needed here. API key = bring your own key: pick any of the ~230 providers OpenCode supports and save its key in the model picker (an OpenAI / Anthropic / Google / DeepSeek key already saved below is reused).'
                    : 'Subscription uses the OAuth credentials JSON from your local CLI install (~/.codex/auth.json). API key uses the provider key.'
              }
            >
              {agent === 'opencode' ? (
                <SegmentedControl
                  value={auth === 'api_key' ? 'api_key' : 'configured'}
                  onChange={(v) => setAuth(v as 'api_key' | 'configured')}
                  options={[
                    { id: 'configured', label: 'OpenCode providers' },
                    { id: 'api_key', label: 'API key' },
                  ]}
                />
              ) : (
                <SegmentedControl
                  value={auth === 'api_key' ? 'api_key' : 'subscription'}
                  onChange={(v) => setAuth(v as 'api_key' | 'subscription')}
                  options={[
                    { id: 'subscription', label: 'Subscription account' },
                    { id: 'api_key', label: 'API key' },
                  ]}
                />
              )}
            </Field>

            <Field
              label="Model"
              hint={
                agent === 'opencode'
                  ? 'Provider / model, as listed by the providers configured in OpenCode on the server. Leave on “Agent default” to use OpenCode’s own default model.'
                  : 'Pick a model from the agent’s provider catalog, or leave on “Agent default” to let the CLI choose.'
              }
            >
              {agent === 'opencode' ? (
                // ~230 providers / ~9k models: a searchable modal, not a select.
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    className="input"
                    onClick={() => setPickerFor('base')}
                    style={{ flex: 1, minWidth: 0, textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title="Search providers and models"
                  >
                    {agentModel || (auth === 'api_key' ? 'Choose a provider and model…' : 'Agent default — click to choose…')}
                  </button>
                  <Button variant="secondary" size="sm" onClick={() => setPickerFor('base')}>
                    {auth === 'api_key' ? 'Providers & keys…' : 'Choose…'}
                  </Button>
                  {auth !== 'api_key' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={refreshAgentModels}
                      disabled={agentModelsLoading}
                      title="Re-read the models from the OpenCode CLI on the server"
                    >
                      {agentModelsLoading ? 'Loading…' : 'Refresh'}
                    </Button>
                  )}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    className="input"
                    value={agentModel}
                    onChange={(e) => setAgentModel(e.target.value)}
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <option value="">Agent default</option>
                    {agentModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {agent === 'opencode' && agentModelsError && (
                <div style={{ fontSize: 13, color: 'var(--color-error, #dc2626)', marginTop: 4 }}>
                  {agentModelsError}
                </div>
              )}
              {agent === 'opencode' && auth === 'api_key' && !agentModel && (
                <div style={{ fontSize: 13, color: 'var(--color-warning, #b45309)', marginTop: 4 }}>
                  API-key mode needs a model — its provider decides which saved key is used.
                </div>
              )}
            </Field>
          </>
        )}

        {/* Params */}
        {((mode === 'api' && (modelSpec?.supports_temperature || modelSpec?.supports_max_tokens)) ||
          mode === 'agent') && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            {mode === 'api' && modelSpec?.supports_temperature && (
              <Field label="Temperature" hint="0.0 – 2.0. Lower = more deterministic.">
                <input
                  type="number"
                  className="input"
                  step="0.1"
                  min={0}
                  max={2}
                  value={temperature}
                  onChange={(e) => setTemperature(e.target.value)}
                  placeholder="Provider default"
                />
              </Field>
            )}
            {mode === 'api' && modelSpec?.supports_max_tokens && (
              <Field label="Max tokens" hint="Cap on output length.">
                <input
                  type="number"
                  className="input"
                  min={1}
                  value={maxTokens}
                  onChange={(e) => setMaxTokens(e.target.value)}
                  placeholder="Provider default"
                />
              </Field>
            )}
          </div>
        )}

        {/* Thinking */}
        {((mode === 'api' && modelSpec?.supports_thinking) ||
          (mode === 'agent' && agentSpec?.supports_thinking)) && (
          <Field
            label="Thinking"
            hint="Reasoning depth. Higher levels are slower and more expensive but produce better answers on complex tasks."
          >
            <SegmentedControl
              value={thinking}
              onChange={(v) => setThinking(v as ThinkingLevel)}
              options={THINKING_LEVELS.map((l) => ({ id: l, label: l }))}
            />
          </Field>
        )}

        {/* Per-task model overrides — one config, different models per task. */}
        <div>
          <button
            type="button"
            onClick={() => setTaskModelsOpen((v) => !v)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-primary)',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              padding: 0,
              width: 'fit-content',
            }}
          >
            {taskModelsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            Per-task model overrides
            {taskOverrideCount > 0 && (
              <span className="badge badge-info" style={{ fontSize: 13, marginLeft: 4 }}>
                {taskOverrideCount} set
              </span>
            )}
          </button>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
            Run individual tasks (FRS, business logic, project modify, …) on a
            different model — the Ask-AI agent switches model automatically per
            task. Leave a row on “Use {baseModelLabel}” to inherit this
            configuration’s model.
          </div>
          <div style={{ marginTop: 8 }}>
            <Button
              variant="secondary"
              size="sm"
              onClick={applyRecommended}
              disabled={recommending}
              title="Fill each task with the cost-optimised recommended model and thinking level for this provider"
            >
              {recommending ? 'Loading…' : 'Use recommended'}
            </Button>
          </div>

          {taskModelsOpen && (
            <div
              style={{
                marginTop: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: 12,
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                background: 'var(--color-bg-elevated)',
              }}
            >
              {TASK_TYPES.map((task) => {
                const ov = taskModels[task] || {}
                return (
                  <div
                    key={task}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <div style={{ width: 170, fontSize: 14, flexShrink: 0 }}>
                      {TASK_LABELS[task]}
                    </div>
                    {mode === 'agent' && agent === 'opencode' ? (
                      <button
                        type="button"
                        className="input"
                        onClick={() => setPickerFor(task)}
                        style={{ flex: 1, minWidth: 0, textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {ov.model || `Use ${baseModelLabel}`}
                      </button>
                    ) : (
                      <select
                        className="input"
                        value={ov.model || ''}
                        onChange={(e) => setTaskOverride(task, { model: e.target.value || undefined })}
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        <option value="">Use {baseModelLabel}</option>
                        {taskModelOptions.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.label}
                          </option>
                        ))}
                      </select>
                    )}
                    {supportsThinkingColumn && (
                      <select
                        className="input"
                        value={ov.thinking && ov.thinking !== 'off' ? ov.thinking : ''}
                        onChange={(e) =>
                          setTaskOverride(task, {
                            thinking: (e.target.value || undefined) as ThinkingLevel | undefined,
                          })
                        }
                        style={{ width: 130, flexShrink: 0 }}
                        title="Thinking level for this task"
                      >
                        <option value="">Default thinking</option>
                        <option value="low">low</option>
                        <option value="medium">medium</option>
                        <option value="high">high</option>
                      </select>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {error && (
          <div
            style={{
              padding: '8px 12px',
              background: 'rgba(220, 38, 38, 0.1)',
              border: '1px solid var(--color-danger, #dc2626)',
              borderRadius: 6,
              color: 'var(--color-danger, #dc2626)',
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}
      </div>
      {mode === 'agent' && agent === 'opencode' && (
        <OpencodeModelPicker
          open={pickerFor !== null}
          onClose={() => setPickerFor(null)}
          auth={auth === 'api_key' ? 'api_key' : 'configured'}
          scope={scope}
          title={pickerFor && pickerFor !== 'base' ? `Model for ${TASK_LABELS[pickerFor]}` : undefined}
          value={pickerFor === 'base' || pickerFor === null ? agentModel : taskModels[pickerFor]?.model || ''}
          allowDefault={pickerFor !== 'base' || auth !== 'api_key'}
          defaultLabel={pickerFor && pickerFor !== 'base' ? `Use ${baseModelLabel}` : undefined}
          onSelect={(id) => {
            if (pickerFor === 'base' || pickerFor === null) setAgentModel(id)
            else setTaskOverride(pickerFor, { model: id || undefined })
          }}
        />
      )}
    </Modal>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label
        style={{
          display: 'block',
          fontSize: 14,
          fontWeight: 600,
          marginBottom: 6,
          color: 'var(--color-text-primary)',
        }}
      >
        {label}
      </label>
      {children}
      {hint && (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 4 }}>
          {hint}
        </div>
      )}
    </div>
  )
}

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T
  onChange: (v: T) => void
  options: { id: T; label: string }[]
}) {
  return (
    <div style={{ display: 'inline-flex', gap: 4, padding: 2, background: 'var(--color-bg-primary)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
      {options.map((opt) => {
        const selected = opt.id === value
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            style={{
              padding: '6px 14px',
              borderRadius: 6,
              border: 'none',
              background: selected ? 'var(--color-accent)' : 'transparent',
              color: selected ? '#fff' : 'var(--color-text-primary)',
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: selected ? 600 : 400,
              textTransform: 'capitalize',
            }}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
